/**
 * A KÉTES KÖTÉS VÁLASZTÓJA — mikor áll a panelen, és miből választ (T19).
 *
 * Két úton nyílik: magától, ha a kulcs kétes és nincs érvényes döntés
 * (`NEEDS_CHOICE`), vagy a könyvelő kérésére egy meglévő döntésből
 * („Újra választok"). A döntés nem visszavonható, csak újra hozható (Viktor,
 * 2026-10-05): így a kulcs soha nem marad döntés nélkül félúton.
 *
 * Tiszta függvény, mert itt dől el, hogy a `1`–`9` és a `0` él-e — a
 * komponensből csak a felrenderelt DOM-ból lehetne visszafejteni.
 */

import type { TensoftBookingView, TensoftInvoiceSummary, TensoftLedgerLine } from './types';

export interface Chooser {
  /** A bizonylat párja. Az „Egyik sem" ezzel adja meg a kulcsot. */
  invoice: TensoftInvoiceSummary;
  candidates: TensoftInvoiceSummary[];
  lines: TensoftLedgerLine[];
  /** A mostani döntés: számla, `null` („Egyik sem"), vagy `undefined`, ha még nincs. */
  currentOwnerId: string | null | undefined;
}

/**
 * A billentyűvel elérhető jelöltek száma. A `0` az „Egyik sem", tehát a
 * számjegyek 1-től 9-ig mennek — a tizedik jelölt csak egérrel választható.
 * Mérve a PontCo-n kettő a jellemző (`tensoft-osszekottetes.md` §3.3/j).
 */
export const KEYED_CANDIDATES = 9;

export function chooserOf(view: TensoftBookingView | null, rechoosing: boolean): Chooser | null {
  if (view === null) return null;
  if (view.state === 'NEEDS_CHOICE') {
    return {
      invoice: view.invoice,
      candidates: view.candidates,
      lines: view.lines,
      currentOwnerId: undefined,
    };
  }
  if (!rechoosing) return null;
  if (
    (view.state === 'BOOKED' && view.decision !== null) ||
    view.state === 'BOOKED_ELSEWHERE' ||
    view.state === 'CHOSEN_NONE'
  ) {
    const decision = view.decision as NonNullable<typeof view.decision>;
    return {
      invoice: view.invoice,
      candidates: decision.candidates,
      lines: decision.lines,
      currentOwnerId: decision.ownerId,
    };
  }
  return null;
}

/** Lehet-e újraválasztani — csak a könyvelő döntéséből, egyértelmű kötésből nem. */
export function canRechoose(view: TensoftBookingView | null): boolean {
  if (view === null) return false;
  if (view.state === 'BOOKED') return view.decision !== null;
  return view.state === 'BOOKED_ELSEWHERE' || view.state === 'CHOSEN_NONE';
}
